import React, { useState } from 'react';
import CircularProgress from '@material-ui/core/CircularProgress';
import Typography from '@material-ui/core/Typography';
import DeleteIcon from '@material-ui/icons/Delete';
import EditIcon from '@material-ui/icons/Edit';
import FileCopyIcon from '@material-ui/icons/FileCopy';
import KeyboardReturnIcon from '@material-ui/icons/KeyboardReturn';
import { ENDPOINTS } from 'features/message-center/constants';
import { useCopyToClipboard, useShowAlert } from 'hooks';
import { SECONDARY_API_CLIENT } from 'services/API';
import strings from 'strings';
import { FPActionButton, FPIconButton, TextBox, When } from 'UI/components/atoms';
import { FPButtonMenu } from 'UI/components/molecules';
import DecisionDialog from 'UI/components/organisms/DecisionDialog';

import {
  ActionsContainer,
  FlexRowBetween,
  StyledIcon,
  TemplateCardContainer,
  TemplateTypography,
  TextBoxContainer,
  TextContainer
} from './TemplateCard.styled';

const COPIES = strings.messageCenter.messageComposer.templates;

export const TemplateCard = ({
  id = null,
  index,
  onClickUseTemplate,
  removeTemplate,
  showUseTemplateButton,
  template,
  title,
  updateTemplates
}) => {
  const isNewTemplate = id === null;

  const { showAlert } = useShowAlert();
  const { copyToClipboard } = useCopyToClipboard();
  const [uiState, setUiState] = useState({
    isEditing: isNewTemplate,
    isLoading: false,
    showConfirm: false
  });
  const [formValues, setFormValues] = useState({
    title,
    template
  });
  const canSave = formValues.title && formValues.template;

  const toggleModal = () => setUiState(prev => ({ ...prev, showConfirm: !prev.showConfirm }));

  const handleChangeForm = (name?, value) => {
    setFormValues(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleClickUseTemplate = () => {
    onClickUseTemplate(template);
  };

  const handleClickEdit = () => {
    setUiState(prev => ({ ...prev, isEditing: true }));
  };

  const handleClickCopy = () => {
    copyToClipboard({ text: formValues.template });
  };

  const handleClickCancel = () => {
    setFormValues({
      title,
      template
    });
    setUiState(prev => ({ ...prev, isEditing: false }));

    if (isNewTemplate) {
      removeTemplate(index);
    }
  };

  const handleClickSave = async () => {
    if (!canSave) return;

    try {
      setUiState(prev => ({ ...prev, isLoading: true }));

      const body = { title: formValues.title, template: formValues.template };
      isNewTemplate
        ? await SECONDARY_API_CLIENT.post(ENDPOINTS.templates, body)
        : await SECONDARY_API_CLIENT.patch(`${ENDPOINTS.templates}/${id}`, body);

      setUiState(prev => ({ ...prev, isLoading: false, isEditing: false }));
      updateTemplates();
    } catch (error) {
      showAlert({
        title: strings.formatString(COPIES.alerts.error, { action: 'saving' }),
        error
      });
      setUiState(prev => ({ ...prev, isLoading: false }));
    }
  };

  const handleClickConfirmDelete = async () => {
    try {
      setUiState(prev => ({ ...prev, isLoading: true }));
      await SECONDARY_API_CLIENT.delete(`${ENDPOINTS.templates}/${id}`);
      updateTemplates();
    } catch (error) {
      setUiState(prev => ({ ...prev, isLoading: false, showConfirm: false }));
      showAlert({
        title: strings.formatString(COPIES.alerts.error, { action: 'deleting' }),
        error
      });
    }
  };

  const menuItems = [
    {
      title: COPIES.form.copy,
      icon: (
        <StyledIcon>
          <FileCopyIcon fontSize="small" />
        </StyledIcon>
      ),
      action: handleClickCopy,
      visible: true
    },
    {
      title: COPIES.form.edit,
      icon: (
        <StyledIcon>
          <EditIcon fontSize="small" />
        </StyledIcon>
      ),
      action: handleClickEdit,
      visible: true
    },
    {
      title: COPIES.form.delete,
      icon: (
        <StyledIcon>
          <DeleteIcon fontSize="small" />
        </StyledIcon>
      ),
      action: toggleModal,
      visible: true
    }
  ];

  return (
    <>
      <TemplateCardContainer aria-label={`template card ${title}`}>
        {uiState.isEditing ? (
          <>
            <TextBoxContainer>
              <TextBox
                name="title"
                autoFocus
                fullWidth
                label={COPIES.form.title}
                noMargin
                onChange={handleChangeForm}
                placeholder={COPIES.form.titlePlaceholder}
                required
                value={formValues.title}
              />

              <TextBox
                name="template"
                fullWidth
                label={COPIES.form.template}
                multiline
                noMargin
                onChange={handleChangeForm}
                placeholder={COPIES.form.contentPlaceholder}
                required
                value={formValues.template}
              />
            </TextBoxContainer>

            <ActionsContainer>
              <FPActionButton
                text={COPIES.form.cancel}
                variant="text"
                onClick={handleClickCancel}
              />
              <FPActionButton
                disabled={!canSave || uiState.isLoading}
                onClick={handleClickSave}
                text={COPIES.form.save}
                variant="contained"
                startIcon={
                  uiState.isLoading ? <CircularProgress color="inherit" size={20} /> : undefined
                }
              />
            </ActionsContainer>
          </>
        ) : (
          <FlexRowBetween>
            <TextContainer>
              <Typography variant="subtitle1">{formValues.title}</Typography>
              <TemplateTypography>{formValues.template}</TemplateTypography>
            </TextContainer>

            <ActionsContainer>
              <When condition={showUseTemplateButton}>
                <FPIconButton
                  disabled={uiState.isLoading}
                  icon={KeyboardReturnIcon}
                  onClick={handleClickUseTemplate}
                  tooltipProps={{ title: COPIES.form.useTemplate }}
                />
              </When>
              <FPButtonMenu
                disabled={uiState.isLoading}
                edge="end"
                isIconButton
                menuItems={menuItems}
                title={strings.shared.rowActions.more}
              />
            </ActionsContainer>
          </FlexRowBetween>
        )}
      </TemplateCardContainer>

      {uiState.showConfirm && (
        <DecisionDialog
          cancelButtonText={COPIES.deleteConfirm.cancelButtonText}
          confirmButtonText={COPIES.deleteConfirm.confirmButtonText}
          fetching={uiState.isLoading}
          isOpened
          message={COPIES.deleteConfirm.message}
          onClose={toggleModal}
          onConfirm={handleClickConfirmDelete}
          severity="error"
          showSeverityIcon
          title={strings.formatString(COPIES.deleteConfirm.title, { title: formValues.title })}
          withButtons="YesNo"
        />
      )}
    </>
  );
};
